<template>
    <header>
        <nav>
            <Disclosure v-slot="{ open }"
                        as="nav"
            >
                <Container>
                    <div class="relative flex h-20 items-center justify-between">
                        <div class="flex items-center grow">
                            <Link :href="route('home')"
                                  class="shrink-0 inline-flex justify-center items-center gap-x-2"
                            >
                                <img :src="Logo"
                                     alt="Dogdom"
                                     class="block h-12"
                                />

                                <span class="italic text-sm text-white font-medium hidden xl:inline-block xl:ml-4">
                                    "the world of dogs and dog enthusiasts"
                                </span>
                            </Link>
                        </div>

                        <div class="flex lg:hidden gap-x-4">
                            <!-- Mobile menu button -->
                            <button class="relative inline-flex items-center justify-center rounded-md bg-brand-600 p-2 text-brand-200 hover:bg-brand-500/75 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-brand-600"
                                    @click="mobileMenuOpen = true"
                            >
                                <span class="absolute -inset-0.5" />
                                <span class="sr-only">Open main menu</span>
                                <Bars3Icon v-if="!open"
                                           aria-hidden="true"
                                           class="block h-6 w-6"
                                />
                                <XMarkIcon v-else
                                           aria-hidden="true"
                                           class="block h-6 w-6"
                                />
                            </button>
                        </div>

                        <div class="hidden lg:ml-4 lg:block">
                            <div class="flex items-center gap-x-1">
                                <template v-for="item in headerNavigation"
                                          :key="item.name"
                                >
                                    <template v-if="!item.notDesktop">
                                        <a v-if="!item.children && item.external"
                                           :aria-current="item.current ? 'page' : undefined"
                                           :class="[item.current ? 'bg-brand-500/75 text-white' : 'text-white hover:bg-brand-500/75', 'rounded-md py-2 px-2 text-sm font-medium whitespace-nowrap']"
                                           :href="item.href"
                                        >
                                            {{ item.name }}
                                        </a>

                                        <Link v-else-if="!item.children"
                                              :aria-current="item.current ? 'page' : undefined"
                                              :class="[item.current ? 'bg-brand-500/75 text-white' : 'text-white hover:bg-brand-500/75', 'rounded-md py-2 px-2 text-sm font-medium whitespace-nowrap']"
                                              :href="item.href"
                                        >
                                            {{ item.name }}
                                        </Link>

                                        <Menu v-else
                                              as="div"
                                              class="relative shrink-0"
                                        >
                                            <div>
                                                <MenuButton :class="[item.current ? 'bg-brand-500/75' : '']"
                                                            class="inline-flex items-center gap-x-1.5 text-white hover:bg-brand-500/75 rounded-md py-2 px-3 text-sm font-medium"
                                                >
                                                    <span class="absolute -inset-1.5" />
                                                    <span class="sr-only">Open menu</span>

                                                    <span class="inline-block max-w-28 truncate">
                                                        {{ item.name }}
                                                    </span>

                                                    <ChevronDownIcon class="h-5 w-5" />
                                                </MenuButton>
                                            </div>

                                            <transition enter-active-class="transition ease-out duration-100"
                                                        enter-from-class="transform opacity-0 scale-95"
                                                        enter-to-class="transform opacity-100 scale-100"
                                                        leave-active-class="transition ease-in duration-75"
                                                        leave-from-class="transform opacity-100 scale-100"
                                                        leave-to-class="transform opacity-0 scale-95"
                                            >
                                                <MenuItems class="absolute left-1/2 -translate-x-1/2 z-10 mt-2 w-screen origin-top-right rounded-md bg-white py-1 shadow-lg focus:outline-none max-w-max">
                                                    <MenuItem v-for="child in item.children"
                                                              :key="child.name"
                                                              v-slot="{ active }"
                                                    >
                                                        <Link v-if="child?.method === 'post'"
                                                              :class="[active ? 'bg-gray-100' : '', 'w-full text-left px-4 py-2 text-sm text-gray-700']"
                                                              :href="child.href"
                                                              as="button"
                                                              method="post"
                                                        >
                                                            {{ child.name }}
                                                        </Link>

                                                        <a v-else-if="child?.external"
                                                           :class="[active ? 'bg-gray-100' : '', 'block px-4 py-2 text-sm text-gray-700']"
                                                           :href="child.href"
                                                        >
                                                            {{ child.name }}
                                                        </a>

                                                        <Link v-else
                                                              :class="[active ? 'bg-gray-100' : '', 'block px-4 py-2 text-sm text-gray-700']"
                                                              :href="child.href"
                                                        >
                                                            {{ child.name }}
                                                        </Link>
                                                    </MenuItem>
                                                </MenuItems>
                                            </transition>
                                        </Menu>
                                    </template>
                                </template>

                                <!-- Profile dropdown -->
                                <Menu v-if="user"
                                      as="div"
                                      class="relative shrink-0"
                                >
                                    <div>
                                        <MenuButton class="inline-flex items-center gap-x-1.5 text-white hover:bg-brand-500/75 rounded-md py-2 px-3 text-sm font-medium">
                                            <span class="absolute -inset-1.5" />
                                            <span class="sr-only">Open user menu</span>

                                            <UserIcon class="h-5 w-5" />

                                            <span class="inline-block max-w-28 truncate">
                                                {{ user.name }}
                                            </span>
                                        </MenuButton>
                                    </div>

                                    <transition enter-active-class="transition ease-out duration-100"
                                                enter-from-class="transform opacity-0 scale-95"
                                                enter-to-class="transform opacity-100 scale-100"
                                                leave-active-class="transition ease-in duration-75"
                                                leave-from-class="transform opacity-100 scale-100"
                                                leave-to-class="transform opacity-0 scale-95"
                                    >
                                        <MenuItems class="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg focus:outline-none">
                                            <MenuItem v-for="item in dropdownNavigation"
                                                      :key="item.name"
                                                      v-slot="{ active }"
                                            >
                                                <Link v-if="item?.method === 'post'"
                                                      :class="[active ? 'bg-gray-100' : '', 'w-full text-left px-4 py-2 text-sm text-gray-700']"
                                                      :href="item.href"
                                                      as="button"
                                                      method="post"
                                                >
                                                    {{ item.name }}
                                                </Link>

                                                <a v-else-if="item?.external"
                                                   :class="[active ? 'bg-gray-100' : '', 'block px-4 py-2 text-sm text-gray-700']"
                                                   :href="item.href"
                                                >
                                                    {{ item.name }}
                                                </a>

                                                <Link v-else
                                                      :class="[active ? 'bg-gray-100' : '', 'block px-4 py-2 text-sm text-gray-700']"
                                                      :href="item.href"
                                                >
                                                    {{ item.name }}
                                                </Link>
                                            </MenuItem>
                                        </MenuItems>
                                    </transition>
                                </Menu>

                                <Link v-else
                                      :href="route('auth.login.index')"
                                      class="relative inline-flex items-center gap-x-1.5 text-white bg-brand-500 rounded-md py-2 px-3 text-sm font-medium"
                                >
                                    <span class="absolute -inset-1.5" />

                                    <UserIcon class="h-5 w-5" />

                                    <span>
                                        Members Area
                                    </span>
                                </Link>

<!--                                <div v-if="$page.props.dogdom.available_locales.length > 1"-->
<!--                                     class="hidden lg:block grow"-->
<!--                                >-->
<!--                                    <div class="flex xl:justify-center space-x-4 w-full">-->
<!--                                        &lt;!&ndash; Country dropdown &ndash;&gt;-->
<!--                                        <Popover class="relative">-->
<!--                                            <PopoverButton :class="['text-white hover:bg-brand-500/75', 'rounded-md py-2 px-3 text-sm font-medium', 'inline-flex items-center gap-x-1']">-->
<!--                                                <ShoppingCartIcon class="h-5 w-5 shrink-0" />-->
<!--                                            </PopoverButton>-->

<!--                                            <transition enter-active-class="transition ease-out duration-200"-->
<!--                                                        enter-from-class="opacity-0 translate-y-1"-->
<!--                                                        enter-to-class="opacity-100 translate-y-0"-->
<!--                                                        leave-active-class="transition ease-in duration-150"-->
<!--                                                        leave-from-class="opacity-100 translate-y-0"-->
<!--                                                        leave-to-class="opacity-0 translate-y-1"-->
<!--                                            >-->
<!--                                                <PopoverPanel class="absolute right-0 z-10 mt-5 flex w-fit">-->
<!--                                                    <div class="w-screen max-w-max flex-auto rounded-3xl bg-white p-4 text-sm leading-6 shadow-lg ring-1 ring-gray-900/5">-->
<!--                                                        <div v-for="availableLocale in $page.props.dogdom.available_locales"-->
<!--                                                             :key="availableLocale.route_key"-->
<!--                                                             class="relative rounded-lg p-2 hover:bg-gray-50 flex items-center gap-x-4"-->
<!--                                                        >-->
<!--                                                            <img :alt="`Flag of ${availableLocale.country.en}`"-->
<!--                                                                 :src="availableLocale.country.flag_url"-->
<!--                                                                 class="w-8 h-auto"-->
<!--                                                            />-->

<!--                                                            <Link :href="route('home', { locale: availableLocale.route_key })"-->
<!--                                                                  class="font-semibold text-gray-900"-->
<!--                                                            >-->
<!--                                                                {{ availableLocale.country.en }}-->

<!--                                                                <span class="absolute inset-0" />-->
<!--                                                            </Link>-->
<!--                                                        </div>-->
<!--                                                    </div>-->
<!--                                                </PopoverPanel>-->
<!--                                            </transition>-->
<!--                                        </Popover>-->
<!--                                    </div>-->
<!--                                </div>-->
                            </div>
                        </div>
                    </div>
                </Container>
            </Disclosure>
        </nav>

        <Dialog :open="mobileMenuOpen"
                class="lg:hidden"
                @close="mobileMenuOpen = false"
        >
            <div class="fixed inset-0 z-10 bg-black/50" />

            <DialogPanel class="fixed inset-y-0 right-0 z-10 w-full overflow-y-auto bg-brand-600 px-4 py-4 sm:max-w-sm sm:ring-1 sm:ring-brand-900/10">
                <div class="flex items-center justify-between">
                    <Link :href="route('home')"
                          class="shrink-0 inline-flex justify-center items-center gap-x-2"
                    >
                        <img :src="Logo"
                             alt="Dogdom"
                             class="block h-12"
                        />
                    </Link>

                    <button class="-m-2.5 rounded-md p-2.5 text-white"
                            type="button"
                            @click="mobileMenuOpen = false"
                    >
                        <span class="sr-only">Close menu</span>
                        <XMarkIcon aria-hidden="true"
                                   class="size-6"
                        />
                    </button>
                </div>

                <div class="mt-6 flow-root">
                    <div class="-my-6 divide-y divide-white">
                        <p class="italic text-base text-white font-medium text-center py-6">
                            "the world of dogs and dog enthusiasts"
                        </p>

                        <div class="space-y-2 py-6">
                            <template v-for="item in headerNavigation"
                                      :key="item.name"
                            >
                                <a v-if="!item.children && item.external"
                                   :aria-current="item.current ? 'page' : undefined"
                                   :href="item.href"
                                   class="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-semibold text-brand-50 hover:text-white"
                                >
                                    {{ item.name }}
                                </a>

                                <Link v-else-if="!item.children"
                                      :aria-current="item.current ? 'page' : undefined"
                                      :href="item.href"
                                      class="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-semibold text-brand-50 hover:text-white"
                                >
                                    {{ item.name }}
                                </Link>

                                <Disclosure v-else
                                            v-slot="{ open }"
                                            as="div"
                                            class="-mx-3"
                                >
                                    <DisclosureButton class="flex w-full items-center justify-between rounded-lg py-2 pl-3 pr-3.5 text-base/7 font-semibold text-brand-50 hover:text-white">
                                        <span>{{ item.name }}</span>

                                        <ChevronDownIcon :class="[open ? 'rotate-180' : '', 'size-5 flex-none']"
                                                         aria-hidden="true"
                                        />
                                    </DisclosureButton>

                                    <DisclosurePanel class="mt-2 space-y-2">
                                        <DisclosureButton v-for="child in item.children"
                                                          :key="child.name"
                                                          :as="Link"
                                                          :href="child.href"
                                                          class="block rounded-lg py-2 pl-6 pr-3 text-sm/7 font-semibold text-brand-50 hover:text-white"
                                        >{{ child.name }}
                                        </DisclosureButton>
                                    </DisclosurePanel>
                                </Disclosure>
                            </template>
                        </div>

                        <div class="space-y-2 py-6">
                            <template v-for="item in dropdownNavigation"
                                      :key="item.name"
                            >
                                <Link v-if="item?.method === 'post'"
                                      :href="item.href"
                                      as="button"
                                      class="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-semibold text-brand-50 hover:text-white"
                                      method="post"
                                >
                                    {{ item.name }}
                                </Link>

                                <a v-else-if="item?.external"
                                   :href="item.href"
                                   class="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-semibold text-brand-50 hover:text-white"
                                >
                                    {{ item.name }}
                                </a>

                                <Link v-else
                                      :href="item.href"
                                      class="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-semibold text-brand-50 hover:text-white"
                                >
                                    {{ item.name }}
                                </Link>
                            </template>
                        </div>
                    </div>
                </div>
            </DialogPanel>
        </Dialog>
    </header>
</template>

<script setup>
import {
    Dialog,
    DialogPanel,
    Disclosure,
    DisclosureButton,
    DisclosurePanel,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    Popover,
    PopoverButton,
    PopoverPanel,
}                               from '@headlessui/vue';
import {
    Bars3Icon,
    XMarkIcon,
}                               from '@heroicons/vue/24/outline';
import {Link}                   from '@inertiajs/vue3';
import {
    ChevronDownIcon,
    UserIcon,
    ShoppingCartIcon,
}                               from '@heroicons/vue/20/solid';
import Container                from '@/js/Components/Container.vue';
import Logo                     from '@/img/logo/logo-light-no-background-no-bleed.png';
</script>

<script>
export default {
    data() {
        return {
            mobileMenuOpen: false,
        };
    },

    computed: {
        headerNavigation: function () {
            return this.assessPermissions(
                [
                    {
                        notDesktop: true,
                        name:       'Change Country',
                        href:       '#',
                        current:    false,
                        children:   this.$page.props.dogdom.available_locales.map((availableLocale) => {
                            return {
                                name:    availableLocale.country.en,
                                href:    route('home', {locale: availableLocale.route_key}),
                                current: false,
                            };
                        }),
                    },

                    {
                        name:    'Litters',
                        href:    route('breed-notices.litter-announcements.index'),
                        current: route().current('breed-notices.litter-announcements.index'),
                    },

                    {
                        name:    'Puppies',
                        href:    route('breed-notices.litter-notices.index'),
                        current: route().current('breed-notices.litter-notices.index'),
                    },

                    {
                        name:    'Breeders',
                        href:    route('breeders.index'),
                        current: [
                                     route().current('breeders.*'),
                                 ].some(isCurrentRoute => isCurrentRoute),
                    },

                    {
                        name:     'Dogs',
                        href:     '#',
                        current:  [
                                      route().current('breeds.*'),
                                      route().current('dogs.*'),
                                  ].some(isCurrentRoute => isCurrentRoute),
                        children: [
                            {
                                name:    'Dog Breeds',
                                href:    route('breeds.index'),
                                current: route().current('breeds.*'),
                            },
                            {
                                name:    'Dog Profiles',
                                href:    route('dogs.index'),
                                current: route().current('dogs.*'),
                            },
                            {
                                name:    'Mature Dogs For Sale',
                                href:    route('breed-notices.mature-dogs.index'),
                                current: route().current('breed-notices.mature-dogs.index'),
                            },
                        ],
                    },

                    {
                        name:    'Clubs',
                        href:    route('clubs.index'),
                        current: [
                                     route().current('clubs.*'),
                                 ].some(isCurrentRoute => isCurrentRoute),
                    },

                    {
                        name:    'Events',
                        href:    route('events.index'),
                        current: [
                                     route().current('events.*'),
                                 ].some(isCurrentRoute => isCurrentRoute) && !route().current('events.show.results.*'),
                    },

                    {
                        name:    'Results',
                        href:    route('results.index'),
                        current: [
                                     route().current('results.*'),
                                     route().current('events.show.results.*'),
                                 ].some(isCurrentRoute => isCurrentRoute),
                    },

                    {
                        name:    '2025 Rankings',
                        href:    route('rankings.index', {year: '2025'}),
                        current: [
                                     route().current('rankings.*', {year: '2025'}),
                                 ].some(isCurrentRoute => isCurrentRoute),
                    },
                ],
            );
        },

        dropdownNavigation: function () {
            return this.assessPermissions(
                [
                    {
                        name:      'Login',
                        href:      route('auth.login.index'),
                        current:   route().current('auth.login.*'),
                        available: !this.user,
                    },

                    {
                        name:      'Register',
                        href:      route('auth.register.index'),
                        current:   route().current('auth.register.*'),
                        available: !this.user,
                    },

                    {
                        name:      'Admin Area',
                        href:      `/admin/${this.$page.props.dogdom.locale.country.iso_code}`,
                        current:   false,
                        external:  true,
                        admin:     true,
                        available: this.user,
                    },

                    {
                        name:      'Members Area',
                        href:      route('my.index'),
                        current:   [
                                       route().current('my.*'),
                                   ].some(isCurrentRoute => isCurrentRoute),
                        available: this.user,
                    },

                    {
                        name:      'My Settings',
                        href:      route('my.settings.index'),
                        available: this.user,
                    },

                    {
                        name:      'Sign Out',
                        href:      route('auth.logout.store'),
                        method:    'post',
                        available: this.user && !!!this.user?.impersonating,
                    },

                    {
                        name:      'Stop Impersonating',
                        href:      route('filament-impersonate.leave'),
                        current:   false,
                        external:  true,
                        available: this.user && !!this.user?.impersonating,
                    },
                ],
            );
        },

        user: function () {
            return this.$page.props.dogdom.user ?? null;
        },
    },

    methods: {
        assessPermissions: function (items) {
            return items
                .filter((item) => {
                    if (item.hasOwnProperty('admin')) {
                        if (this.$page.props.dogdom?.user?.is_admin !== true) {
                            return false;
                        }
                    }

                    if (item.hasOwnProperty('available')) {
                        return item.available;
                    }

                    return true;
                })
                .map((item) => {
                    if (item.hasOwnProperty('children')) {
                        item = {
                            ...item,
                            children: this.assessPermissions(item.children),
                        };
                    }

                    return item;
                });
        },
    },
};
</script>